<script lang="ts" context="module">
  import { getAppConfig } from '../../../common/config/app-config';
  import { selectCorrectSmartPrice, selectCorrectSmartPromoBoolean } from '../../../common/joodUtils';
  import {
    formatNumber,
    getJoodExpiredMembershipType,
    getJoodMembershipType,
    getStr,
    globalCurrencyTag,
    roundToNDigits,
    type JoodMembershipType,
  } from '../../../common/util';
  import { getBottomTags } from '../../brand/services/brand-data-services';
  import Icon from '../../common/components/Icon.svelte';
  import JoodExpire from '../../common/components/productTile/components/JoodExpire.svelte';
  import { ProductTilePriceGroupVariantType } from '../../common/components/productTile/product-tile-main';
  import TimerTag from '../../common/components/tag/TimerTag.svelte';

  const {
    encodedContextPath,
    extraThemeResourcePath,
    joodExpiredFlag,
    countryCode,
    unbxdSearchConfig,
    lang,
    joodFullCappingLimitReached,
  } = getAppConfig();
  const joodSubscribeLinkHref = `${encodedContextPath}/jood/benefits`;
  let userJoodMembershipType: JoodMembershipType = getJoodMembershipType();
  const expiredJoodMembershipType = getJoodExpiredMembershipType();
</script>

<script lang="ts">
  export let algoliaProductHit;
  export let bannerType: JoodMembershipType;
  export let variant: ProductTilePriceGroupVariantType;
  export let backgoundOverride: JoodMembershipType = null;
  if (expiredJoodMembershipType && bannerType === expiredJoodMembershipType) {
    userJoodMembershipType = bannerType = expiredJoodMembershipType;
  }

  let backgroundImage,
    foregroundImage,
    title,
    PriceColor,
    titleBorderColor,
    joodDayPriceCardBorderColor,
    timerColor,
    joodCardImageUrl,
    percentageBg,
    percentageColor,
    incVatColor,
    savePriceColor,
    titleColor,
    backgroundPosition;
  const isTacticalVipPromo = algoliaProductHit?.tacticalVipPromo;
  const endDateJoodDay = algoliaProductHit?.endDateJoodDay;
  const discountDecimals = +unbxdSearchConfig.decimalCount;
  const joodDayRemainingQuantity = algoliaProductHit?.joodDayRemainingQuantity;
  $: visible = getJoodDayBannerVisibility(
    bannerType,
    userJoodMembershipType,
    algoliaProductHit,
    joodFullCappingLimitReached
  );
  $: ({ backgroundImage, foregroundImage } = getJoodDayBannerDetails(backgoundOverride ?? userJoodMembershipType));
  $: ({
    title,
    PriceColor,
    titleBorderColor,
    joodDayPriceCardBorderColor,
    timerColor,
    joodCardImageUrl,
    percentageBg,
    percentageColor,
    incVatColor,
    savePriceColor,
    titleColor,
    backgroundPosition,
  } = getJoodDayThemeConfigs(backgoundOverride ?? userJoodMembershipType));

  $: displayPrice = getPrice(bannerType, algoliaProductHit);
  $: wasPrice = getWasPrice(bannerType, algoliaProductHit);
  $: savedPrice = wasPrice - displayPrice;
  $: subscribeLinkVisible = getSubscribeLinkVisibility(bannerType, userJoodMembershipType);
  $: subscribeLinkText = getSubscribeLinkText(bannerType, userJoodMembershipType);
  $: hasSavings = getHasSavings(bannerType, userJoodMembershipType);
  $: savedPercentVisible = getSavedPercentVisibility(algoliaProductHit);
  $: savedPercent = hasSavings && roundToNDigits((savedPrice * 100) / wasPrice, discountDecimals);

  // 2025-11-15T23:59:59Z
  // 2025-11-14T20:59:59+0000

  const dealObj = { endDateDeal: endDateJoodDay };
  const bottomTags = { tagsData: getBottomTags(dealObj) };
  const tagData = { product: bottomTags.tagsData[0].product };
  const isJoodDeal = endDateJoodDay ? true : false;
  function getSavedPercentVisibility(algoliaProductHit) {
    return countryCode !== 'OM' || algoliaProductHit?.priceValueDiscountPercentageFlag;
  }

  function getHasSavings(bannerType: JoodMembershipType, userJoodMembershipType: JoodMembershipType) {
    let visibility = false;
    if (
      userJoodMembershipType !== 'BASICPRIME' &&
      userJoodMembershipType !== 'VIPPRIME' &&
      userJoodMembershipType !== 'SMARTPRIME'
    ) {
      visibility = savedPrice > 0 && bannerType === 'NOPRIME' && !isTacticalVipPromo;
    } else {
      if (joodExpiredFlag === 'BASIC' || joodExpiredFlag === 'VIP') {
        visibility = savedPrice > 0 && bannerType === 'NOPRIME';
      } else {
        visibility = savedPrice > 0;
      }
    }
    return visibility;
  }

  // Utility functions

  function getJoodDayBannerVisibility(
    bannerType: JoodMembershipType,
    userJoodMembershipType: JoodMembershipType,
    algoliaHit: any,
    joodFullCappingLimitReached: string
  ) {
    const { tacticalBasicPromo, tacticalBasicPrimePrice, tacticalVipPromo, tacticalVipPrimePrice } = algoliaHit;
    const tacticalSmartPromo = selectCorrectSmartPromoBoolean(
      algoliaHit?.tacticalSmartFirstPromo,
      algoliaHit?.tacticalSmartRepeatingPromo
    );
    const tacticalSmartPrimePrice = selectCorrectSmartPrice(
      algoliaHit?.smartFirstPrimePrice,
      algoliaHit?.smartRepeatingPrimePrice
    );
    const hasTacticalPromo =
      (tacticalBasicPromo && tacticalBasicPrimePrice > 0 && bannerType === 'BASICPRIME') ||
      (tacticalVipPromo && tacticalVipPrimePrice > 0 && bannerType === 'VIPPRIME') ||
      (tacticalSmartPromo && tacticalSmartPrimePrice > 0 && bannerType === 'SMARTPRIME');

    /*
            Rules:
            1. if gray member, hide blue banner
            2. if blue member, hide nothing
            3. if gold member, hide blue banner
        */
    let bannerVisible = true;
    if (userJoodMembershipType === 'NOPRIME' || userJoodMembershipType === 'VIPPRIME') {
      bannerVisible = bannerType !== 'BASICPRIME';
    }

    return hasTacticalPromo && bannerVisible && joodFullCappingLimitReached !== 'true';
  }
  function getJoodDayBannerDetails(userJoodMembershipType: JoodMembershipType) {
    let suffix = 'NONPRIME';
    if (userJoodMembershipType === 'BASICPRIME' || joodExpiredFlag === 'BASIC') suffix = 'BASIC';
    else if (userJoodMembershipType === 'VIPPRIME' || joodExpiredFlag === 'VIP') suffix = 'VIP';
    else suffix = 'NONPRIME';
    const bgKey = `prime.jood.day.bg.${suffix}`,
      fgKey = `prime.jood.day.logo.${suffix}`;

    const backgroundImage = getStr(bgKey),
      foregroundImage = getStr(fgKey);

    return { backgroundImage, foregroundImage };
  }
  function getTitle(bannerType: JoodMembershipType) {
    switch (bannerType) {
      case 'BASICPRIME':
        return getStr('extra.jood.blue.banner.title');
      case 'VIPPRIME':
        return getStr('extra.jood.gold.banner.title');
      case 'SMARTPRIME':
        return getStr('extra.jood.smart.banner.title');
      default:
        return getStr('extra.jood.gold.banner.title');
    }
  }
  function getTitleKey(joodType) {
    if (joodDayRemainingQuantity > 0) {
      if (variant === 'pdp') {
        return `${getStr('extra.joodday.short.desc.pdp', [joodDayRemainingQuantity])}`;
      } else {
        return `extra.joodday.short.desc.${joodType}`;
      }
    } else {
      return getTitle(bannerType);
    }
  }
  function getJoodDayThemeConfigs(userJoodMembershipType: JoodMembershipType) {
    const joodType = userJoodMembershipType ? userJoodMembershipType : 'NOPRIME';
    const priceColorKey = `extra.joodday.price.color.${joodType}`,
      titleBorderKey = `extra.joodday.heading.border.${joodType}`,
      priceCardBorderKey = `extra.joodday.pricecard.border.${joodType}`,
      titleKey = getTitleKey(joodType),
      titleColorKey = `extra.joodday.short.desc.color.${joodType}`,
      timerColorKey = `extra.joodday.timer.text.color.${joodType}`,
      joodRightImgKey = `extra.joodday.right.img.${joodType}`,
      percentageBgKey = `extra.joodday.percentage.bg.${joodType}`,
      percentageColorKey = `extra.joodday.percentage.color.${joodType}`,
      incVatKey = `extra.joodday.vattext.color.${joodType}`,
      savePriceKey = `extra.joodday.saveprice.color.${joodType}`,
      positionKey = `extra.joodday.bottom.tag.bg.position.${joodType}`;
    const PriceColor = getStr(priceColorKey),
      titleBorderColor = getStr(titleBorderKey),
      joodDayPriceCardBorderColor = getStr(priceCardBorderKey),
      title = getStr(titleKey),
      timerColor = getStr(timerColorKey),
      joodCardImageUrl = getStr(joodRightImgKey),
      percentageBg = getStr(percentageBgKey),
      percentageColor = getStr(percentageColorKey),
      incVatColor = getStr(incVatKey),
      savePriceColor = getStr(savePriceKey),
      titleColor = getStr(titleColorKey),
      backgroundPosition = getStr(positionKey);
    return {
      title,
      PriceColor,
      titleBorderColor,
      joodDayPriceCardBorderColor,
      timerColor,
      joodCardImageUrl,
      percentageBg,
      percentageColor,
      incVatColor,
      savePriceColor,
      titleColor,
      backgroundPosition,
    };
  }

  function getPrice(bannerType: JoodMembershipType, algoliaProductHit: any) {
    let sourcePrice;
    switch (bannerType) {
      case 'NOPRIME':
        sourcePrice = algoliaProductHit.tacticalVipPrimePrice;
        break;
      case 'BASICPRIME':
        sourcePrice = algoliaProductHit.tacticalBasicPrimePrice;
        break;
      case 'VIPPRIME':
        sourcePrice = algoliaProductHit.tacticalVipPrimePrice;
        break;
      case 'SMARTPRIME':
        sourcePrice = selectCorrectSmartPrice(
          algoliaProductHit?.tacticalSmartFirstPrimePrice,
          algoliaProductHit?.tacticalSmartRepeatingPrimePrice
        );
        break;
    }

    return sourcePrice;
  }
  function getWasPrice(bannerType: JoodMembershipType, algoliaProductHit: any) {
    let sourcePrice;
    switch (bannerType) {
      case 'NOPRIME':
        sourcePrice = algoliaProductHit.vipPrimePrice;
        break;
      case 'BASICPRIME':
        sourcePrice = algoliaProductHit.basicPrimePrice;
        break;
      case 'VIPPRIME':
        sourcePrice = algoliaProductHit.vipPrimePrice;
        break;
      case 'SMARTPRIME':
        sourcePrice = selectCorrectSmartPrice(
          algoliaProductHit?.smartFirstPrimePrice,
          algoliaProductHit?.smartRepeatingPrimePrice
        );
        break;
    }
    return sourcePrice;
  }
  function getSubscribeLinkVisibility(bannerType, userJoodMembershipType) {
    /*
            Rules:
            1. if gray member, subscribe link on gold banner
            2. if blue member, subscribe link on gold banner
            3. if gold member, no subscribe link
        */
    if (variant === 'pdp' && backgoundOverride) return true;

    let visibility = false;
    if (userJoodMembershipType === 'NOPRIME' || userJoodMembershipType === 'BASICPRIME') {
      visibility = bannerType === 'VIPPRIME'; // rule 1 & 2
    } else {
      visibility = false; // rule 3
    }

    return visibility;
  }
  function getSubscribeLinkText(bannerType, userJoodMembershipType) {
    /*
            Rules:
            1. if gray member, subscribe text on gold price
            2. if blue member, upgrade text on gold price
            3. if gold member, no subscribe text
        */
    let text: string = ''; // rule 3
    if (
      (userJoodMembershipType === 'NOPRIME' || userJoodMembershipType === 'BASICPRIME') &&
      bannerType === 'VIPPRIME'
    ) {
      text = getStr('extra.prime.become.jood.member.start.saving.text'); // rule 1 & 2
    }

    return text;
  }
  $: displayPriceFormat = `<strong>${formatNumber(displayPrice)}</strong>`;
</script>

{#if visible}
  <section
    class="jood-day-banner-new variant-{variant} {joodExpiredFlag
      ? 'expired-card'
      : ''} user-{userJoodMembershipType} expired-{joodExpiredFlag} banner-{bannerType}"
    class:c_joodlimitedstockpromo={tagData}
  >
    <section
      style="border: {joodDayPriceCardBorderColor} solid 1px; background-image: url('{backgroundImage}'); background-position: {backgroundPosition};"
      class="jood-day-plp-banner"
    >
      <section class="banner-logo" style:border-color={titleBorderColor}>
        <div class="logo-img">
          <img src={foregroundImage} alt="" />
        </div>
        {#if isJoodDeal}
          <div class="c_joodtimer" style="color:{timerColor}">
            <Icon type="font" glyph="working-hours" mobileSize={15} desktopSize={18} />
            <section class="c_jooddealtime" style="color:{timerColor}">
              <TimerTag {...tagData} {isJoodDeal} />
            </section>
          </div>
        {:else}
          <div class="jood-day-timer">
            <img src={`${extraThemeResourcePath}/images/jood/extra-calender.svg`} alt="" />
            <span style="color:{timerColor};">{getStr('extra.price.today.only.text')}</span>
          </div>
        {/if}
      </section>
      <section class="banner-details">
        <section class="banner-details-left">
          <h4 style="color:{titleColor}">{@html title}</h4>
          <section class="price" style:color={PriceColor}>
            {@html getStr('jood.price.with.currency', [globalCurrencyTag, displayPriceFormat])}
            <span class="vat" style="color:{incVatColor}">{getStr('extra.addtocart.inc.of.vat')}</span>
          </section>
          {#if hasSavings}
            <section class="savings">
              <span class="striked-off" style:color={PriceColor}>{formatNumber(wasPrice)}</span>
              <strong style:color={savePriceColor} class="yellow-text"
                >{@html getStr('extra.jood.saved.amt', [globalCurrencyTag, formatNumber(savedPrice)])}</strong
              >
            </section>

            {#if savedPercentVisible}
              <section class="save-percent-tag" style:background-color={percentageBg} style:color={percentageColor}>
                {getStr('product.percentageDiscount.off', [savedPercent])}
              </section>
            {/if}
          {/if}
        </section>
        {#if joodCardImageUrl}
          <div class="c_joodclock">
            <img src={joodCardImageUrl} alt="Joodday" />
          </div>
        {/if}
      </section>
      {#if bannerType !== 'NOPRIME'}
        <JoodExpire place="price-expire-card" />
      {/if}
      {#if subscribeLinkVisible && !joodExpiredFlag}
        <section class="subscribe-upgrade-bottom">
          <a class="subscribe-upgrade-link" on:click|stopPropagation href={joodSubscribeLinkHref}>
            {subscribeLinkText}
            <Icon type="font" glyph={lang === 'en' ? 'chevron_right' : 'chevron_left'} mobileSize={12} />
          </a>
        </section>
      {/if}
    </section>
  </section>
{/if}

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  .jood-day-banner-new {
    border-radius: 12px;
    flex: 1 1 calc(50% - 8px);
    letter-spacing: 0;

    .jood-day-plp-banner {
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      color: $primary-white;
      background-size: cover;

      .banner-logo {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 5px 0 2px;
        @include responsive {
          padding: 2px 5px 2px 2px;
        }
        .logo-img {
          width: 90px;
          @include responsive {
            width: 60px;
          }
        }
        .jood-day-timer {
          display: flex;
          align-items: center;
          gap: 4px;
          padding-right: 4px;
          img {
            width: 20px;
          }
          span {
            color: transparent;
            font-weight: 500;
            font-size: 14px;
            line-height: 16px;
            @include responsive {
              font-size: 10px;
            }
          }
        }
      }
      .banner-details {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding: 12px 4px 12px 8px;
        color: $neutral-dark-01;

        .banner-details-left {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          flex-grow: 1;
          h4 {
            font-weight: 500;
            font-size: 12px;
            line-height: 16px;
            margin: 0;
          }
          .price {
            font-weight: 700;
            font-size: 20px;
            line-height: 20px;
            letter-spacing: 0px;
            @include responsive {
              font-size: 12px;
            }

            .vat {
              font-size: 10px;
              font-weight: 400;
              line-height: 14px;
            }
          }
          .savings {
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .striked-off {
            font-weight: 400;
            font-size: 12px;
            line-height: 14px;
            text-decoration: line-through;
          }
          .orange-text {
            color: $functional-orange;
            font-size: 12px;
            line-height: 14px;
            @include responsive {
              font-size: 11px;
              line-height: 18px;
            }
          }
          .red-text {
            color: $alert-danger;
            font-size: 12px;
            line-height: 14px;
          }
          .yellow-text {
            color: $primary-yellow-01;
            font-size: 12px;
            line-height: 14px;
          }
          .save-percent-tag {
            display: inline-block;
            padding: 4px 4px 2px;
            border-radius: 3px;
            color: $primary-white;
            font-weight: 700;
            font-size: 10px;
            line-height: 10px;
            background-color: $functional-orange;
            margin-top: 4px;
            text-transform: uppercase;

            @include responsive {
              font-size: 9px;
              line-height: 10px;
              padding: 2px 5px;
            }
          }
        }
        .banner-details-right {
          display: flex;
          justify-content: end;
          align-items: center;
          text-align: end;
          img {
            max-width: 82px;
            box-shadow: 0px 4px 4px 0px #00000026;
            border-radius: 8px;
            max-height: 58px;
          }
        }
      }
    }
    &.banner-BASICPRIME {
      .jood-day-plp-banner {
        border: 1px solid $jood-blue-border;
        .banner-logo {
          border-bottom: 1px solid $jood-blue-border;
        }
        .banner-details {
          color: $jood-blue-text;
          .banner-details-left {
            h4 {
              font-weight: 500;
            }
            .save-percent-tag {
              position: relative;
              right: 0px;
              top: 0px;
              margin-top: 4px;
              margin-bottom: 2px;
              background-color: $primary-yellow-01;
              color: $black;
            }
          }
        }
      }
    }

    &.banner-VIPPRIME {
      .jood-day-plp-banner {
        border: 1px solid #265ba7;
        .banner-logo {
          border-bottom: 1px solid #265ba7;
        }
        .banner-details {
          color: $jood-gold-border;
          .banner-details-left {
            h4 {
              font-weight: 500;
            }
            .save-percent-tag {
              position: relative;
              right: 0px;
              top: 0px;
              margin-top: 4px;
              margin-bottom: 4px;
              background-color: $primary-yellow-01;
              color: $black;
            }
          }
        }
      }
    }

    &.banner-SMARTPRIME {
      .jood-day-plp-banner {
        border: 1px solid $jood-smart-border;
        .banner-logo {
          border-bottom: 1px solid $jood-smart-border;
        }
        .banner-details {
          color: $jood-smart-border;
          .banner-details-left {
            h4 {
              font-weight: 500;
            }
            .save-percent-tag {
              position: relative;
              right: 0px;
              top: 0px;
              margin-top: 4px;
              margin-bottom: 4px;
              background-color: $alert-danger;
            }
          }
        }
      }
    }

    &.user-NOPRIME {
      .jood-day-plp-banner {
        .banner-details {
          .banner-details-right {
            img {
              max-width: 63.8px;
              max-height: 45.6px;
              box-shadow: 0px 0px 8px 0px #00000033;
            }
          }
        }
      }
    }

    .subscribe-upgrade-bottom {
      padding: 2px 8px;
      background: $primary-white;
      text-align: center;
      border-radius: 0px 0px 11px 11px;
      .subscribe-upgrade-link {
        display: flex;
        justify-content: center;
        font-weight: 700;
        color: #0065a4;
        font-size: 12px;
        line-height: 16px;
        gap: 2px;
      }
    }

    &.variant-product-tile {
      @include responsive {
        .c_joodclock {
          width: 80px !important;
        }
      }
      &.user-NOPRIME,
      &.user-VIPPRIME.expired-VIP,
      &.user-SMARTPRIME.expired-SMART,
      &.user-BASICPRIME.expired-BASIC {
        .banner-logo {
          img {
            width: 90px;
          }

          .c_jooddealtime {
            font-size: 14px;
          }
          .jood-day-timer {
            img {
              width: 16px;
              margin-top: -4px;
            }
            span {
              font-size: 12px;
            }
          }
        }
        .banner-details {
          .banner-details-left {
            gap: 8px;
            @include responsive {
              gap: 6px;
            }
          }
          .banner-details-right {
            img {
              border-radius: 6px;
            }
          }
        }
      }
      &.user-NOPRIME {
        @include responsive {
          .c_joodclock {
            width: 54px !important;
          }
        }
        .banner-logo .jood-day-timer span {
          font-size: 14px;
        }
        .banner-details-left {
          padding-top: 6px;
        }
      }
      &.user-VIPPRIME,
      &.user-SMARTPRIME,
      &.user-BASICPRIME {
        .banner-details {
          padding-top: 10px;
          h4 {
            font-size: 14px !important;
          }
        }
        .jood-day-timer span {
          font-size: 16px;
        }
        .c_joodclock {
          width: 80px !important;
        }
        .banner-details-left {
          .price {
            margin: 7px 0 2px 0;
          }
        }
      }
    }

    &.variant-pdp {
      &.user-NOPRIME,
      &.user-VIPPRIME.expired-VIP,
      &.user-SMARTPRIME.expired-SMART,
      &.user-BASICPRIME.expired-BASIC {
        .c_jooddealtime {
          font-size: 16px;
          @include responsive {
            font-size: 11px;
          }
        }
        .banner-logo {
          .logo-img {
            width: 117px;
            @include responsive {
              width: 88px;
              padding: 0;
            }
          }
          .jood-day-timer {
            @include responsive {
              gap: 2px;
            }
            img {
              width: 20px;
              @include responsive {
                width: 15px;
              }
            }
            span {
              font-size: 16px;
              @include responsive {
                font-size: 11px;
              }
            }
          }
        }
        .banner-details {
          width: 100%;
          padding: 12px 4px 12px 12px;
          @include responsive {
            padding: 8px 0 15px 8px;
          }
          .banner-details-left {
            flex-basis: 65%;
            @include responsive {
              flex-basis: 60%;
            }
            h4 {
              font-size: 16px;
              line-height: 16px;
              letter-spacing: 0;
              @include responsive {
                font-size: 11px;
              }
            }
            .price {
              font-size: 30px;
              line-height: 22px;
              margin-top: 12px;
              @include responsive {
                font-size: 20px;
                line-height: 20px;
                margin-top: 4px;
              }
              .vat {
                font-size: 10px;
                @include responsive {
                  font-size: 6px;
                }
              }
              .savings {
                .striked-off {
                  @include responsive {
                    font-size: 10px;
                    line-height: 12px;
                  }
                }
                .save-percent-tag {
                  @include responsive {
                    font-size: 10px;
                    line-height: 18px;
                    padding: 2px 4px;
                  }
                }
              }
            }
          }
          .banner-details-right {
            flex-basis: 35%;
            img {
              max-width: 77.7px;
              max-height: 57.4px;
              border-radius: 8px;
              @include responsive {
                max-width: 52px;
                max-height: 37px;
                border-radius: 4px;
              }
            }
          }
        }
        .subscribe-upgrade-bottom {
          padding: 4px;
          @include responsive {
            padding: 2px;
          }
          .subscribe-upgrade-link {
            font-size: 12px;
            @include responsive {
              font-size: 9px;
              gap: 0px;
            }
          }
        }
      }

      &.user-VIPPRIME,
      &.user-BASICPRIME,
      &.user-SMARTPRIME {
        @include desktop-min-width {
          .price {
            margin-top: 10px !important;
            margin-bottom: 4px;
          }
        }
        .banner-logo {
          .c_jooddealtime {
            font-size: 18px;
            @include responsive {
              font-size: 16px;
            }
          }
          .logo-img {
            width: 142px;
            @include responsive {
              width: 110px;
            }
          }
          .jood-day-timer {
            @include responsive {
              gap: 4px;
            }
            img {
              width: 26px;
              @include responsive {
                width: 20px;
              }
            }
            span {
              font-size: 18px;
              @include responsive {
                font-size: 16px;
              }
            }
          }
        }
        .banner-details {
          padding: 12px 4px 12px 12px;
          .banner-details-left {
            h4 {
              font-size: 20px;
              @include responsive {
                font-size: 12px;
              }
              :global(span) {
                font-size: 26px;
                @include responsive {
                  font-size: 16px;
                }
              }
            }
            .price {
              margin-top: 4px;
              font-size: 28px;
              line-height: 32px;
              @include responsive {
                font-size: 24px;
              }
            }
            .vat {
              font-size: 12px;
              @include responsive {
                line-height: 12px;
              }
            }
            .striked-off {
              font-size: 16px;
              line-height: 18px;
              @include responsive {
                font-size: 12px;
                line-height: 12px;
              }
            }
            .red-text {
              font-size: 16px;
              line-height: 14px;
              @include responsive {
                font-size: 12px;
                line-height: 12px;
              }
            }
          }
          .banner-details-right {
            flex-basis: 40%;
            img {
              max-width: 127.1px;
              max-height: 90.7px;
              border-radius: 12px;
              @include responsive {
                max-width: 107.1px;
                max-height: 76px;
                border-radius: 8px;
              }
            }
          }
        }
        .subscribe-upgrade-bottom {
          padding: 4px 8px;
        }
      }
    }

    &.variant-plp-list {
      @include responsive {
        flex: 1 1 calc(100% - 8px);
      }
      &.user-NOPRIME,
      &.user-VIPPRIME.expired-VIP,
      &.user-BASICPRIME.expired-BASIC,
      &.user-SMARTPRIME.expired-SMART {
        .banner-logo {
          img {
            width: 90px;
            @include responsive {
              width: 90px;
              padding: 0;
            }
          }
          .jood-day-timer {
            img {
              margin-top: -3px;
              width: 15px;
            }
            span {
              font-size: 12px;
              @include responsive {
                font-size: 14px;
              }
            }
          }
        }
        .banner-details {
          align-items: center;
          width: 100%;
          padding: 8px 4px 12px 8px;
          @include responsive {
            padding: 10px 4px 10px 8px;
          }
          .banner-details-left {
            flex-basis: 65%;
            @include responsive {
              flex-basis: 70%;
            }
            h4 {
              font-size: 12px;
              line-height: 16px;
              letter-spacing: 0;

              @include responsive {
                font-size: 14px;
              }
            }
            .price {
              font-size: 20px;
              line-height: 20px;
              margin-top: 8px;
              @include responsive {
                margin-top: 4px;
              }
            }
            .vat {
              font-size: 8px;
              @include responsive {
                font-size: 9px;
              }
            }
            .savings {
              .striked-off {
                @include responsive {
                  font-size: 10px;
                  line-height: 12px;
                }
              }
              .save-percent-tag {
                @include responsive {
                  font-size: 10px;
                  line-height: 18px;
                  padding: 2px 4px;
                }
              }
            }
          }
          .banner-details-right {
            flex-basis: 35%;
            @include responsive {
              flex-basis: 30%;
            }
            img {
              border-radius: 6px;
              max-width: 59px;
              max-height: 42px;
              @include responsive {
                max-width: 78px;
                max-height: 56px;
                border-radius: 8px;
              }
            }
          }
        }
        .subscribe-upgrade-bottom {
          @include responsive {
            padding: 2px;
          }
          .subscribe-upgrade-link {
            font-size: 9px;
            @include responsive {
              font-size: 10px;
            }
          }
        }
      }

      &.user-VIPPRIME,
      &.user-BASICPRIME,
      &.user-SMARTPRIME {
        .banner-logo {
          img {
            width: 100px;
            @include responsive {
              width: 90px;
            }
          }
          .jood-day-timer {
            img {
              width: 20px;
            }
            span {
              font-size: 16px;
              @include responsive {
                font-size: 14px;
              }
            }
          }
        }
        .banner-details {
          padding: 10px 4px 12px 8px;
          .banner-details-left {
            flex-basis: 70%;
            h4 {
              font-size: 16px;
              @include responsive {
                font-size: 12px;
              }
            }
            .price {
              margin-top: 6px;
              margin-bottom: 6px;
              font-size: 22px;
              line-height: 26px;
              @include responsive {
                font-size: 20px;
                line-height: 24px;
                margin-top: 2px;
                margin-bottom: 4px;
              }
            }
            .vat {
              @include responsive {
                font-size: 10px;
                line-height: 12px;
              }
            }
            .striked-off {
              font-size: 14px;
              line-height: 14px;
              @include responsive {
                font-size: 12px;
                line-height: 14px;
              }
            }
            .red-text {
              font-size: 14px;
              line-height: 14px;
              @include responsive {
                font-size: 12px;
                line-height: 14px;
              }
            }
          }
          .banner-details-right {
            flex-basis: 30%;
            @include responsive {
              flex-basis: 40%;
            }
            img {
              max-width: 96px;
              max-height: 68px;
              border-radius: 10px;
              @include responsive {
                max-width: 82px;
                max-height: 58px;
                border-radius: 8px;
              }
            }
          }
        }
      }
    }
    &.variant-plp-grid {
      .jood-day-plp-banner .banner-details .banner-details-left h4 {
        @include responsive {
          font-size: 9px;
        }
      }
      .c_joodclock {
        @include responsive {
          display: none;
        }
      }
    }
  }
  :global(.jood-addtocart-popup .jood-day-banner-new .banner-logo img) {
    width: 115px !important;
    @include responsive {
      width: 90px !important;
    }
  }
  :global(.jood-addtocart-popup .jood-day-banner-new .banner-logo .jood-day-timer img) {
    width: 20px !important;
  }
  :global(.jood-addtocart-popup .jood-day-banner-new .banner-logo .jood-day-timer span) {
    font-size: 14px !important;
    @include responsive {
      font-size: 12px !important;
    }
  }
  :global(.jood-addtocart-popup .jood-day-banner-new .subscribe-upgrade-bottom .subscribe-upgrade-link) {
    font-size: 12px !important;
    @include responsive {
      font-size: 10px !important;
    }
  }

  :global(.jood-addtocart-popup .jood-day-banner-new .banner-details .banner-details-left h4) {
    font-size: 18px !important;
    @include responsive {
      font-size: 14px !important;
    }
  }
  :global(.jood-addtocart-popup .jood-day-banner-new .banner-details .banner-details-left .price) {
    font-size: 24px !important;
    line-height: 30px !important;
  }
  :global(.jood-addtocart-popup .jood-day-banner-new .banner-details .banner-details-left .vat) {
    font-size: 12px !important;
  }
  //pricecard jood styles
  :global {
    .tile-content-wrapper {
      .banner-details {
        .banner-details-left {
          // gap: 8px;
          h4 {
            font-size: 13px !important;
          }
        }
      }
      .logo-img {
        width: 94px;
      }
      .c_jooddealtime {
        font-size: 14px;
        line-height: 16px;
      }
      .c_joodclock {
        gap: 3px;
        height: auto !important;
        width: 70px !important;
        @include responsive {
          width: 54px !important;
        }
      }
      .user-NOPRIME {
        .banner-details {
          min-height: 73px;
          padding: 6px 4px 7px 8px !important;
          @include responsive {
            min-height: 67px;
          }
        }
      }
    }
    @include responsive {
      .smallprodtile.rr_parent_item_container {
        .jood-day-banner-new.variant-product-tile .c_joodclock {
          width: 58px !important;
        }
      }
    }
  }
</style>
